/**
 * InvoiceListView — list/table view with create, filter, and pagination.
 */

import { FileText, Globe, FilePlus, Receipt as ReceiptIcon, Plus } from 'lucide-react'

import { INVOICE_FEATURES, INVOICE_STEPS, INVOICE_WHAT_IS_IT } from './invoices-helpers'
import { InvoiceList } from '../../components/billing'
import { InvoiceIllustration } from '../../components/illustrations/AccountingIllustrations'
import { CollapsibleListInfo, EmptyState } from '../../components/ui'

import type { InvoiceActionsState } from './useInvoiceActions'
import type { Invoice } from '../../types/billing'
import type { FilterBarCondition } from '@elsapiens/ui'

interface PageProps {
  invoices: Invoice[]
  isLoading: boolean
  isLoadingMore: boolean
  isProforma: boolean
  isVendor: boolean
  searchQuery: string
  setSearchQuery: (q: string) => void
  sortField: string
  setSortField: (f: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (d: 'asc' | 'desc') => void
  activeTab: string
  setActiveTab: (t: string) => void
  conditions: FilterBarCondition[]
  setConditions: (c: FilterBarCondition[]) => void
  hasFilters: boolean
  page: number
  setPage: (p: number) => void
  totalPages: number
  totalCount: number
  sumTotalAmount: number
  sumTaxAmount: number
  currencyOptions: { value: string; label: string }[]
}

interface Props {
  page: PageProps
  actions: InvoiceActionsState
}

function buildEmptyStateNode(isVendor: boolean, featureNodes: ReturnType<typeof buildFeatureNodes>, handleCreate: () => void): JSX.Element {
  return (
    <EmptyState
      description={
        isVendor
          ? 'Vendor bills track what you owe to suppliers. Create your first bill to start managing payables.'
          : 'Invoices are formal payment requests to customers. Create your first invoice to start tracking receivables.'
      }
      features={featureNodes}
      gettingStarted={INVOICE_STEPS}
      illustration={<InvoiceIllustration size="lg" />}
      primaryAction={{
        label: isVendor ? 'Create Bill' : 'Create Invoice',
        icon: <Plus className="w-4 h-4 mr-2" />,
        onClick: handleCreate,
      }}
      title={isVendor ? 'No vendor bills found' : 'No invoices found'}
      whatIsIt={INVOICE_WHAT_IS_IT}
    />
  )
}

function buildCollapsibleInfoNode(isVendor: boolean, featureNodes: ReturnType<typeof buildFeatureNodes>): JSX.Element {
  const description = isVendor
    ? 'Vendor bills record what you owe to suppliers. Each posted bill creates a journal entry for Accounts Payable.'
    : 'Invoices are formal payment requests sent to customers. Each posted invoice updates your receivable balances.'
  return (
    <CollapsibleListInfo
      description={description}
      features={featureNodes}
      gettingStarted={INVOICE_STEPS}
      illustration={<InvoiceIllustration size="lg" />}
      title={isVendor ? 'About Vendor Bills' : 'About Invoices'}
      whatIsIt={INVOICE_WHAT_IS_IT}
    />
  )
}

export function InvoiceListView({ page, actions }: Props): JSX.Element {
  const featureNodes = buildFeatureNodes()
  const { isVendor } = page

  const serverFilters = {
    invoices: page.invoices,
    searchQuery: page.searchQuery,
    setSearchQuery: page.setSearchQuery,
    sortField: page.sortField,
    setSortField: page.setSortField,
    sortDirection: page.sortDirection,
    setSortDirection: page.setSortDirection,
    activeTab: page.activeTab,
    setActiveTab: page.setActiveTab,
    conditions: page.conditions,
    setConditions: page.setConditions,
    hasFilters: page.hasFilters,
    page: page.page,
    setPage: page.setPage,
    totalPages: page.totalPages,
    totalCount: page.totalCount,
    sumTotalAmount: page.sumTotalAmount,
    sumTaxAmount: page.sumTaxAmount,
    currencyOptions: page.currencyOptions,
  }

  return (
    <InvoiceList
      collapsibleInfoNode={buildCollapsibleInfoNode(isVendor, featureNodes)}
      emptyStateNode={buildEmptyStateNode(isVendor, featureNodes, actions.handleCreate)}
      isLoading={page.isLoading}
      isLoadingMore={page.isLoadingMore}
      isProforma={page.isProforma}
      isVendor={isVendor}
      serverFilters={serverFilters}
      onCreate={actions.handleCreate}
      onEdit={actions.handleEdit}
      onGeneratePDF={(invoice: Invoice) => { void actions.handleGeneratePDF(invoice) }}
      onPost={actions.handlePost}
      onView={actions.handleView}
      onVoid={actions.handleVoid}
    />
  )
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- TODO(refactor): define explicit return type
function buildFeatureNodes() {
  const icons = [
    <FileText key="file-text" className="w-4 h-4" />,
    <ReceiptIcon key="receipt" className="w-4 h-4" />,
    <Globe key="globe" className="w-4 h-4" />,
    <FilePlus key="file-plus" className="w-4 h-4" />,
  ]
  return INVOICE_FEATURES.map((f, i) => ({ ...f, icon: icons[i] }))
}
